import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CalendarGrid from './components/CalendarGrid';
import EventDetailsModal from './components/EventDetailsModal';

export interface CalendarEvent {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  time?: string;
  location?: string;
  category: string;
  level: 'GC' | 'Division' | 'Union' | 'National';
  levelName: string;
  union?: string;
  image?: string;
  attendees?: number;
  isOnline?: boolean;
}

export default function Events() {
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'year'>('year');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedLevels, setSelectedLevels] = useState<string[]>(['GC', 'Division', 'Union', 'National']);
  const [selectedUnions, setSelectedUnions] = useState<string[]>(['All']);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Comprehensive event data with hierarchical levels
  const allEvents: CalendarEvent[] = [
    // ========================================
    // GENERAL CONFERENCE SPECIAL DAYS 2026
    // ========================================
    
    // JANUARY 2026
    {
      id: 101,
      title: 'Quarterly Day of Prayer',
      description: 'A special day set aside for united prayer across the global Adventist church, seeking God\'s guidance and blessing for the new quarter.',
      startDate: '2026-01-03',
      endDate: '2026-01-03',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://public.readdy.ai/ai/img_res/1fd536ec821326b85b7916c10609a722.jpg',
      isOnline: true,
    },
    {
      id: 102,
      title: 'Ten Days of Prayer',
      description: 'Global prayer initiative uniting Adventists worldwide in focused prayer and spiritual renewal, seeking revival and reformation.',
      startDate: '2026-01-07',
      endDate: '2026-01-17',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20global%20prayer%20gathering%20with%20people%20from%20different%20nations%20kneeling%20in%20prayer%20warm%20spiritual%20atmosphere%20candles%20soft%20lighting%20peaceful%20worship%20setting%20simple%20background&width=800&height=500&seq=gc-prayer-ten-days-2026&orientation=landscape',
      isOnline: true,
    },
    {
      id: 103,
      title: 'Health Ministries Day',
      description: 'Celebrating health ministries and promoting wholistic wellness through physical, mental, and spiritual health initiatives.',
      startDate: '2026-01-10',
      endDate: '2026-01-10',
      category: 'Health',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 104,
      title: 'Religious Liberty Day',
      description: 'Emphasizing the importance of religious freedom and the separation of church and state worldwide.',
      startDate: '2026-01-17',
      endDate: '2026-01-17',
      category: 'Advocacy',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // FEBRUARY 2026
    {
      id: 105,
      title: 'Reach the World: Personal Outreach',
      description: 'Empowering church members to share their faith through personal relationships and one-on-one ministry.',
      startDate: '2026-02-07',
      endDate: '2026-02-07',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 106,
      title: 'Christian Home and Marriage Week',
      description: 'A week dedicated to strengthening marriages and families through biblical principles and practical guidance.',
      startDate: '2026-02-14',
      endDate: '2026-02-21',
      category: 'Family',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Happy%20diverse%20families%20together%20in%20warm%20home%20setting%20parents%20and%20children%20united%20in%20love%20peaceful%20atmosphere%20family%20togetherness%20simple%20background&width=800&height=500&seq=gc-family-week-2026&orientation=landscape',
      isOnline: true,
    },

    // MARCH 2026
    {
      id: 107,
      title: 'Women\'s Day of Prayer',
      description: 'A special day dedicated to women\'s prayer ministries, encouraging women worldwide to unite in prayer and spiritual fellowship.',
      startDate: '2026-03-07',
      endDate: '2026-03-07',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20women%20in%20prayer%20circle%20holding%20hands%20peaceful%20worship%20setting%20warm%20lighting%20spiritual%20unity%20feminine%20strength%20simple%20background&width=800&height=500&seq=gc-women-prayer-2026&orientation=landscape',
      isOnline: true,
    },
    {
      id: 108,
      title: 'Adventist World Radio (AWR) Day',
      description: 'Celebrating the global radio ministry that shares the gospel message in multiple languages, reaching unreached people groups worldwide.',
      startDate: '2026-03-14',
      endDate: '2026-03-14',
      category: 'Media',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 109,
      title: 'Youth Week of Prayer',
      description: 'A week dedicated to youth spiritual growth, featuring daily devotionals and prayer meetings designed specifically for young people.',
      startDate: '2026-03-21',
      endDate: '2026-03-28',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 110,
      title: 'Global Youth Day',
      description: 'Worldwide celebration empowering Adventist youth to serve their communities and share their faith through practical service projects.',
      startDate: '2026-03-21',
      endDate: '2026-03-21',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Energetic%20global%20youth%20celebration%20with%20diverse%20young%20people%20serving%20communities%20colorful%20banners%20modern%20worship%20vibrant%20atmosphere%20unity%20and%20service%20simple%20background&width=800&height=500&seq=gc-youth-day-2026&orientation=landscape',
      isOnline: false,
    },
    {
      id: 111,
      title: 'Global Children\'s Day',
      description: 'Celebrating children\'s ministries worldwide and emphasizing the importance of nurturing faith in young hearts.',
      startDate: '2026-03-21',
      endDate: '2026-03-21',
      category: 'Children',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Joyful%20children%20from%20diverse%20backgrounds%20celebrating%20together%20colorful%20balloons%20happy%20faces%20worship%20activities%20playful%20atmosphere%20simple%20background&width=800&height=500&seq=gc-children-day-2026&orientation=landscape',
      isOnline: false,
    },
    {
      id: 112,
      title: 'Christian Education Day',
      description: 'Highlighting the importance of Christian education and supporting Adventist schools and educational institutions.',
      startDate: '2026-03-28',
      endDate: '2026-03-28',
      category: 'Education',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // APRIL 2026
    {
      id: 113,
      title: 'Quarterly Day of Prayer',
      description: 'A special day set aside for united prayer across the global Adventist church, seeking God\'s guidance and blessing for the new quarter.',
      startDate: '2026-04-04',
      endDate: '2026-04-04',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 114,
      title: 'World Ambassador Day',
      description: 'Recognizing and commissioning church members as ambassadors for Christ in their communities and workplaces.',
      startDate: '2026-04-04',
      endDate: '2026-04-04',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 115,
      title: 'Literature Evangelism Rally Week',
      description: 'A week dedicated to promoting literature evangelism and supporting those who share faith through printed materials.',
      startDate: '2026-04-11',
      endDate: '2026-04-17',
      category: 'Evangelism',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 116,
      title: 'Friends of Hope Day (Visitor\'s Day)',
      description: 'Special Sabbath inviting friends and community members to experience Adventist worship and fellowship.',
      startDate: '2026-04-11',
      endDate: '2026-04-11',
      category: 'Evangelism',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 117,
      title: 'Hope Channel International Day',
      description: 'Celebrating the global television ministry that broadcasts hope and biblical messages to millions worldwide.',
      startDate: '2026-04-11',
      endDate: '2026-04-11',
      category: 'Media',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 118,
      title: 'World Impact Day (Missionary Book Distribution)',
      description: 'Global initiative distributing missionary books to communities, sharing hope and biblical truth through literature.',
      startDate: '2026-04-11',
      endDate: '2026-04-11',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 119,
      title: 'Possibility Ministries Day',
      description: 'Highlighting ministries that serve individuals with disabilities, promoting inclusion and accessibility in church life.',
      startDate: '2026-04-18',
      endDate: '2026-04-18',
      category: 'Ministry',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // MAY 2026
    {
      id: 120,
      title: 'Drug Awareness Month',
      description: 'Month-long emphasis on drug prevention, education, and supporting those affected by substance abuse through health ministries.',
      startDate: '2026-05-02',
      endDate: '2026-05-30',
      category: 'Health',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Health%20awareness%20campaign%20with%20educational%20materials%20community%20support%20groups%20wellness%20activities%20hope%20and%20recovery%20theme%20simple%20background&width=800&height=500&seq=gc-drug-awareness-2026&orientation=landscape',
      isOnline: true,
    },
    {
      id: 121,
      title: 'Reach the World: Using Communication Channels',
      description: 'Exploring modern communication methods and digital platforms for sharing the gospel message effectively.',
      startDate: '2026-05-02',
      endDate: '2026-05-02',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 122,
      title: 'Global Adventurer\'s Day',
      description: 'Celebrating the Adventurer club ministry for children ages 6-9, promoting character development and spiritual growth.',
      startDate: '2026-05-16',
      endDate: '2026-05-16',
      category: 'Children',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 123,
      title: 'World Day of Prayer for Children at Risk',
      description: 'Global prayer initiative focusing on children facing poverty, abuse, trafficking, and other vulnerabilities.',
      startDate: '2026-05-23',
      endDate: '2026-05-23',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },

    // JUNE 2026
    {
      id: 124,
      title: 'Reach the World: Bible Study (Sabbath School & Correspondence)',
      description: 'Promoting Bible study methods through Sabbath School programs and correspondence courses worldwide.',
      startDate: '2026-06-06',
      endDate: '2026-06-06',
      category: 'Education',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 125,
      title: 'Women\'s Ministries Emphasis Day',
      description: 'Highlighting the vital role of women in church ministry and leadership, celebrating their contributions and gifts.',
      startDate: '2026-06-13',
      endDate: '2026-06-13',
      category: 'Women',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 126,
      title: 'Reach the World: Nurturing Members & Reclaiming',
      description: 'Focusing on member retention, spiritual nurture, and reclaiming inactive members through intentional ministry.',
      startDate: '2026-06-20',
      endDate: '2026-06-20',
      category: 'Ministry',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 127,
      title: 'Adventist Church World Refugee Day',
      description: 'Raising awareness about refugee crises and supporting displaced persons through compassionate ministry.',
      startDate: '2026-06-20',
      endDate: '2026-06-20',
      category: 'Humanitarian',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 128,
      title: 'World Public Campus Ministries Day',
      description: 'Recognizing campus ministries that reach students on public university campuses with the gospel message.',
      startDate: '2026-06-27',
      endDate: '2026-06-27',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // JULY 2026
    {
      id: 129,
      title: 'Quarterly Day of Prayer',
      description: 'A special day set aside for united prayer across the global Adventist church, seeking God\'s guidance and blessing for the new quarter.',
      startDate: '2026-07-04',
      endDate: '2026-07-04',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 130,
      title: 'Missions Promotion Day',
      description: 'Highlighting global mission work and encouraging support for mission projects and missionaries worldwide.',
      startDate: '2026-07-11',
      endDate: '2026-07-11',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 131,
      title: 'Reach the World: Media Ministry',
      description: 'Exploring the use of media platforms including television, radio, and digital channels for evangelism.',
      startDate: '2026-07-18',
      endDate: '2026-07-18',
      category: 'Media',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 132,
      title: 'Children\'s Sabbath',
      description: 'A special Sabbath dedicated to celebrating children and emphasizing their importance in the church family.',
      startDate: '2026-07-25',
      endDate: '2026-07-25',
      category: 'Children',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // AUGUST 2026
    {
      id: 133,
      title: 'Global Mission Evangelism Day',
      description: 'Worldwide evangelistic initiative focusing on reaching unreached people groups and planting new churches.',
      startDate: '2026-08-01',
      endDate: '2026-08-01',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Global%20mission%20outreach%20with%20diverse%20missionaries%20serving%20communities%20evangelism%20activities%20cross-cultural%20ministry%20hope%20and%20compassion%20simple%20background&width=800&height=500&seq=gc-mission-2026&orientation=landscape',
      isOnline: false,
    },
    {
      id: 134,
      title: 'Reach the World: Church Planting',
      description: 'Special emphasis on church planting strategies and supporting new congregations in unreached areas.',
      startDate: '2026-08-08',
      endDate: '2026-08-08',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 135,
      title: 'Education Day',
      description: 'Celebrating Adventist education and supporting schools, teachers, and students in their academic and spiritual journey.',
      startDate: '2026-08-15',
      endDate: '2026-08-15',
      category: 'Education',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 136,
      title: 'enditnow® Day',
      description: 'Global initiative raising awareness about abuse and violence, promoting healing and prevention through church ministry.',
      startDate: '2026-08-22',
      endDate: '2026-08-22',
      category: 'Advocacy',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 137,
      title: 'Lay Evangelism Day',
      description: 'Celebrating and empowering lay members to share their faith through personal evangelism and community outreach.',
      startDate: '2026-08-22',
      endDate: '2026-08-22',
      category: 'Evangelism',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // SEPTEMBER 2026
    {
      id: 138,
      title: 'Youth Spiritual & Mission Commitment Day',
      description: 'A day for young people to renew their spiritual commitment and dedicate themselves to mission service.',
      startDate: '2026-09-05',
      endDate: '2026-09-05',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 139,
      title: 'Family Togetherness Week of Prayer',
      description: 'A week dedicated to strengthening family bonds through prayer, worship, and quality time together.',
      startDate: '2026-09-06',
      endDate: '2026-09-12',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20families%20praying%20together%20warm%20home%20setting%20parents%20and%20children%20united%20in%20worship%20love%20and%20togetherness%20peaceful%20atmosphere%20simple%20background&width=800&height=500&seq=gc-family-prayer-2026&orientation=landscape',
      isOnline: true,
    },
    {
      id: 140,
      title: 'Family Togetherness Day of Prayer',
      description: 'Special day of prayer focusing on family unity, spiritual growth, and strengthening family relationships.',
      startDate: '2026-09-12',
      endDate: '2026-09-12',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 141,
      title: 'Mission Promotion Day',
      description: 'Highlighting global mission work and encouraging support for mission projects and missionaries worldwide.',
      startDate: '2026-09-12',
      endDate: '2026-09-12',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 142,
      title: 'Pathfinder Day',
      description: 'Celebrating the Pathfinder club ministry that develops leadership and character in youth through outdoor activities and service.',
      startDate: '2026-09-19',
      endDate: '2026-09-19',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 143,
      title: 'Sabbath School Guest Day',
      description: 'Special Sabbath encouraging members to invite friends and family to experience Sabbath School fellowship and Bible study.',
      startDate: '2026-09-26',
      endDate: '2026-09-26',
      category: 'Sabbath School',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // OCTOBER 2026
    {
      id: 144,
      title: 'Quarterly Day of Prayer',
      description: 'A special day set aside for united prayer across the global Adventist church, seeking God\'s guidance and blessing for the new quarter.',
      startDate: '2026-10-03',
      endDate: '2026-10-03',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 145,
      title: 'Adventist Review Subscription Promotion',
      description: 'Promoting the official church magazine and encouraging members to stay connected through church publications.',
      startDate: '2026-10-03',
      endDate: '2026-10-03',
      category: 'Media',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 146,
      title: 'Pastor Appreciation Day',
      description: 'A day to honor and express gratitude for pastors and their faithful service to the church and community.',
      startDate: '2026-10-10',
      endDate: '2026-10-10',
      category: 'Leadership',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 147,
      title: 'Spirit of Prophecy & Adventist Heritage Day',
      description: 'Celebrating the prophetic gift and rich heritage of the Seventh-day Adventist Church.',
      startDate: '2026-10-17',
      endDate: '2026-10-17',
      category: 'Heritage',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 148,
      title: 'Creation Sabbath',
      description: 'Celebrating God as Creator and emphasizing stewardship of the earth and its resources.',
      startDate: '2026-10-24',
      endDate: '2026-10-24',
      category: 'Worship',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // NOVEMBER 2026
    {
      id: 149,
      title: 'Week of Prayer',
      description: 'Annual week dedicated to spiritual reflection, prayer, and deepening relationship with God through daily devotionals.',
      startDate: '2026-11-07',
      endDate: '2026-11-14',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Peaceful%20prayer%20gathering%20with%20diverse%20believers%20kneeling%20in%20worship%20soft%20evening%20light%20spiritual%20atmosphere%20unity%20in%20prayer%20simple%20background&width=800&height=500&seq=gc-week-prayer-2026&orientation=landscape',
      isOnline: true,
    },
    {
      id: 150,
      title: 'e-Week of Prayer (Youth & Young Adults)',
      description: 'Digital prayer week designed specifically for youth and young adults, featuring online devotionals and virtual gatherings.',
      startDate: '2026-11-14',
      endDate: '2026-11-20',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 151,
      title: 'Mission Promotion Day',
      description: 'Highlighting global mission work and encouraging support for mission projects and missionaries worldwide.',
      startDate: '2026-11-14',
      endDate: '2026-11-14',
      category: 'Mission',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 152,
      title: 'World Orphans & Vulnerable Children Day',
      description: 'Raising awareness and supporting ministries that care for orphans and vulnerable children worldwide.',
      startDate: '2026-11-21',
      endDate: '2026-11-21',
      category: 'Humanitarian',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 153,
      title: 'HIV/AIDS Awareness Day',
      description: 'Promoting education, prevention, and compassionate care for those affected by HIV/AIDS.',
      startDate: '2026-11-28',
      endDate: '2026-11-28',
      category: 'Health',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 154,
      title: 'Stewardship Revival Week',
      description: 'A week dedicated to renewing commitment to faithful stewardship of time, talents, and resources.',
      startDate: '2026-11-28',
      endDate: '2026-12-05',
      category: 'Stewardship',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },

    // DECEMBER 2026
    {
      id: 155,
      title: 'Annual Stewardship Sabbath',
      description: 'Special Sabbath emphasizing faithful stewardship of time, talents, and resources for God\'s kingdom.',
      startDate: '2026-12-05',
      endDate: '2026-12-05',
      category: 'Stewardship',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },
    {
      id: 156,
      title: 'Health Emphasis Day',
      description: 'Promoting wholistic health principles and encouraging healthy lifestyle choices in the church community.',
      startDate: '2026-12-12',
      endDate: '2026-12-12',
      category: 'Health',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // ========================================
    // EXISTING 2025 EVENTS
    // ========================================
    
    // General Conference Events (Highest Priority)
    {
      id: 1,
      title: 'Ten Days of Prayer',
      description: 'Global prayer initiative uniting Adventists worldwide in focused prayer and spiritual renewal.',
      startDate: '2025-01-08',
      endDate: '2025-01-18',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20global%20prayer%20gathering%20with%20people%20from%20different%20nations%20kneeling%20in%20prayer%20warm%20spiritual%20atmosphere%20candles%20soft%20lighting%20peaceful%20worship%20setting&width=800&height=500&seq=gc-prayer-001&orientation=landscape',
      isOnline: true,
    },
    {
      id: 2,
      title: 'Global Youth Day',
      description: 'Worldwide celebration empowering Adventist youth to serve their communities and share their faith.',
      startDate: '2025-03-15',
      endDate: '2025-03-15',
      category: 'Youth',
      level: 'GC',
      levelName: 'General Conference',
      image: 'https://readdy.ai/api/search-image?query=Energetic%20global%20youth%20celebration%20with%20diverse%20young%20people%20serving%20communities%20colorful%20banners%20modern%20worship%20vibrant%20atmosphere%20unity%20and%20service&width=800&height=500&seq=gc-youth-002&orientation=landscape',
      isOnline: false,
    },
    {
      id: 3,
      title: 'Week of Prayer',
      description: 'Annual week dedicated to spiritual reflection, prayer, and deepening relationship with God.',
      startDate: '2025-11-08',
      endDate: '2025-11-15',
      category: 'Prayer',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: true,
    },
    {
      id: 4,
      title: 'World Sabbath School Day',
      description: 'Celebrating Sabbath School ministry and its impact on spiritual growth worldwide.',
      startDate: '2025-10-25',
      endDate: '2025-10-25',
      category: 'Sabbath School',
      level: 'GC',
      levelName: 'General Conference',
      isOnline: false,
    },

    // West-Central Africa Division Events
    {
      id: 5,
      title: 'WAD Youth Congress',
      description: 'West-Central Africa Division youth gathering for leadership training and spiritual empowerment.',
      startDate: '2025-07-20',
      endDate: '2025-07-25',
      location: 'Accra, Ghana',
      category: 'Youth',
      level: 'Division',
      levelName: 'West-Central Africa Division',
      image: 'https://readdy.ai/api/search-image?query=Large%20African%20youth%20congress%20with%20thousands%20of%20young%20people%20in%20modern%20convention%20center%20energetic%20worship%20colorful%20African%20attire%20dynamic%20stage%20production&width=800&height=500&seq=wad-youth-003&orientation=landscape',
      attendees: 8000,
      isOnline: false,
    },
    {
      id: 6,
      title: 'WAD Ministerial Council',
      description: 'Strategic gathering of pastors and church leaders across West-Central Africa for ministry excellence.',
      startDate: '2025-05-10',
      endDate: '2025-05-14',
      location: 'Abidjan, Ivory Coast',
      category: 'Leadership',
      level: 'Division',
      levelName: 'West-Central Africa Division',
      attendees: 500,
      isOnline: true,
    },
    {
      id: 7,
      title: 'WAD Women\'s Convention',
      description: 'Empowering women leaders across West-Central Africa through faith-based training and fellowship.',
      startDate: '2025-09-05',
      endDate: '2025-09-08',
      location: 'Lagos, Nigeria',
      category: 'Women',
      level: 'Division',
      levelName: 'West-Central Africa Division',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20African%20women%20convention%20with%20beautifully%20dressed%20Nigerian%20women%20in%20traditional%20attire%20sophisticated%20venue%20inspiring%20speakers%20warm%20atmosphere&width=800&height=500&seq=wad-women-004&orientation=landscape',
      attendees: 3000,
      isOnline: false,
    },

    // Union-Level Events (Nigeria)
    {
      id: 8,
      title: 'National Youth Congress 2025',
      description: 'Annual gathering of Adventist youth from across Nigeria for spiritual renewal and leadership training.',
      startDate: '2025-12-15',
      endDate: '2025-12-18',
      time: '9:00 AM - 6:00 PM',
      location: 'National Stadium, Abuja',
      category: 'Youth',
      level: 'Union',
      levelName: 'Nigeria Union Mission',
      union: 'Nigeria Union',
      image: 'https://readdy.ai/api/search-image?query=Massive%20Nigerian%20youth%20conference%20with%20thousands%20of%20young%20people%20in%20stadium%20setting%20colorful%20stage%20production%20energetic%20worship%20contemporary%20atmosphere&width=800&height=500&seq=union-youth-005&orientation=landscape',
      attendees: 5000,
      isOnline: false,
    },
    {
      id: 9,
      title: 'Pathfinder Camporee',
      description: 'National Pathfinder camping event featuring outdoor skills, spiritual activities, and fellowship.',
      startDate: '2025-08-10',
      endDate: '2025-08-15',
      location: 'Jos, Plateau State',
      category: 'Youth',
      level: 'Union',
      levelName: 'Nigeria Union Mission',
      union: 'Nigeria Union',
      attendees: 2000,
      isOnline: false,
    },
    {
      id: 10,
      title: 'Evangelism Training Workshop',
      description: 'Equipping church members across Nigeria with practical evangelism skills and biblical knowledge.',
      startDate: '2025-02-05',
      endDate: '2025-02-07',
      time: '8:00 AM - 4:00 PM',
      location: 'Ibadan Training Center',
      category: 'Training',
      level: 'Union',
      levelName: 'Nigeria Union Mission',
      union: 'Nigeria Union',
      attendees: 500,
      isOnline: true,
    },
    {
      id: 11,
      title: 'Health & Wellness Expo',
      description: 'Promoting healthy living through health screenings, nutrition workshops, and wellness seminars.',
      startDate: '2025-03-10',
      endDate: '2025-03-10',
      time: '9:00 AM - 6:00 PM',
      location: 'Port Harcourt Exhibition Center',
      category: 'Health',
      level: 'Union',
      levelName: 'Nigeria Union Mission',
      union: 'Nigeria Union',
      image: 'https://readdy.ai/api/search-image?query=Modern%20health%20expo%20with%20exhibition%20booths%20health%20screening%20stations%20fitness%20demonstrations%20vibrant%20atmosphere%20professional%20medical%20equipment%20Nigerian%20attendees&width=800&height=500&seq=union-health-006&orientation=landscape',
      attendees: 1500,
      isOnline: false,
    },

    // ========================================
    // NIGERIA NATIONAL HOLIDAYS 2025
    // ========================================
    {
      id: 12,
      title: 'New Year\'s Day',
      description: 'National public holiday celebrating the beginning of the new year.',
      startDate: '2025-01-01',
      endDate: '2025-01-01',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 13,
      title: 'Workers\' Day',
      description: 'National public holiday honoring workers and their contributions to society.',
      startDate: '2025-05-01',
      endDate: '2025-05-01',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 14,
      title: 'National Children\'s Day',
      description: 'National public holiday celebrating children and their importance to Nigeria\'s future.',
      startDate: '2025-05-27',
      endDate: '2025-05-27',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 15,
      title: 'Democracy Day',
      description: 'National public holiday commemorating Nigeria\'s return to democratic governance.',
      startDate: '2025-06-12',
      endDate: '2025-06-12',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 16,
      title: 'Independence Day',
      description: 'National public holiday celebrating Nigeria\'s independence from colonial rule.',
      startDate: '2025-10-01',
      endDate: '2025-10-01',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },

    // ========================================
    // NIGERIA NATIONAL HOLIDAYS 2026
    // ========================================
    {
      id: 201,
      title: 'New Year\'s Day',
      description: 'National public holiday celebrating the beginning of the new year.',
      startDate: '2026-01-01',
      endDate: '2026-01-01',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 202,
      title: 'Workers\' Day',
      description: 'National public holiday honoring workers and their contributions to society.',
      startDate: '2026-05-01',
      endDate: '2026-05-01',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 203,
      title: 'National Children\'s Day',
      description: 'National public holiday celebrating children and their importance to Nigeria\'s future.',
      startDate: '2026-05-27',
      endDate: '2026-05-27',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 204,
      title: 'Democracy Day',
      description: 'National public holiday commemorating Nigeria\'s return to democratic governance.',
      startDate: '2026-06-12',
      endDate: '2026-06-12',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
    {
      id: 205,
      title: 'Independence Day',
      description: 'National public holiday celebrating Nigeria\'s independence from colonial rule.',
      startDate: '2026-10-01',
      endDate: '2026-10-01',
      category: 'National Holiday',
      level: 'National',
      levelName: 'Nigeria National Holiday',
    },
  ];

  // Filter events based on selected levels and unions
  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      const levelMatch = selectedLevels.includes(event.level);
      const unionMatch = event.level !== 'Union' || selectedUnions.includes('All') || (event.union && selectedUnions.includes(event.union));
      return levelMatch && unionMatch;
    });
  }, [allEvents, selectedLevels, selectedUnions]);

  const toggleLevel = (level: string) => {
    setSelectedLevels(prev =>
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  const toggleUnion = (union: string) => {
    if (union === 'All') {
      setSelectedUnions(['All']);
    } else {
      const newUnions = selectedUnions.includes(union)
        ? selectedUnions.filter(u => u !== union)
        : [...selectedUnions.filter(u => u !== 'All'), union];
      
      setSelectedUnions(newUnions.length === 0 ? ['All'] : newUnions);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (viewMode === 'month') {
        newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      } else if (viewMode === 'week') {
        newDate.setDate(prev.getDate() + (direction === 'next' ? 7 : -7));
      } else {
        newDate.setFullYear(prev.getFullYear() + (direction === 'next' ? 1 : -1));
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const levelColors = {
    GC: 'bg-navy-100 text-navy-800 border-navy-300',
    Division: 'bg-blue-100 text-blue-800 border-blue-300',
    Union: 'bg-amber-100 text-amber-800 border-amber-300',
    National: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  };

  // Auto-open event modal from URL parameter
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    if (eventId) {
      const event = allEvents.find(e => e.id === parseInt(eventId));
      if (event) {
        setSelectedEvent(event);
        // Remove the eventId from URL after opening modal
        searchParams.delete('eventId');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Church Events Calendar
            </h1>
            <p className="text-lg text-white/95 max-w-3xl mx-auto">
              View events from General Conference, Division, Union, and National levels
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          {/* View Mode & Navigation */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Today
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateMonth('prev')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button
                  onClick={() => navigateMonth('next')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>

              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                {viewMode === 'year' 
                  ? currentDate.getFullYear()
                  : currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                }
              </h2>
            </div>

            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('month')}
                className={`px-4 py-2 rounded-md text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'month' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-calendar-line mr-2"></i>
                Month
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 rounded-md text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'week' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-calendar-week-line mr-2"></i>
                Week
              </button>
              <button
                onClick={() => setViewMode('year')}
                className={`px-4 py-2 rounded-md text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'year' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-calendar-2-line mr-2"></i>
                Year
              </button>
            </div>
          </div>

          {/* Level Filters */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Levels</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => toggleLevel('GC')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  selectedLevels.includes('GC')
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-800 border-slate-300 hover:bg-slate-50'
                }`}
              >
                <i className="ri-global-line mr-2"></i>
                General Conference
              </button>
              <button
                onClick={() => toggleLevel('Division')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  selectedLevels.includes('Division')
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                }`}
              >
                <i className="ri-earth-line mr-2"></i>
                West-Central Africa Division
              </button>
              <button
                onClick={() => toggleLevel('Union')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  selectedLevels.includes('Union')
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-amber-600 border-amber-300 hover:bg-amber-50'
                }`}
              >
                <i className="ri-community-line mr-2"></i>
                Union Missions
              </button>
              <button
                onClick={() => toggleLevel('National')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  selectedLevels.includes('National')
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-emerald-600 border-emerald-300 hover:bg-emerald-50'
                }`}
              >
                <i className="ri-flag-line mr-2"></i>
                National Holidays
              </button>
            </div>
          </div>

          {/* Union Filters */}
          {selectedLevels.includes('Union') && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Union Missions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => toggleUnion('All')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    selectedUnions.includes('All')
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Unions
                </button>
                <button
                  onClick={() => toggleUnion('Eastern Nigeria Union Conference (ENUC)')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    selectedUnions.includes('Eastern Nigeria Union Conference (ENUC)')
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Eastern Nigeria Union (ENUC)
                </button>
                <button
                  onClick={() => toggleUnion('Northern Nigeria Union Conference (NNUC)')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    selectedUnions.includes('Northern Nigeria Union Conference (NNUC)')
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Northern Nigeria Union (NNUC)
                </button>
                <button
                  onClick={() => toggleUnion('Western Nigeria Union Conference (WNUC)')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    selectedUnions.includes('Western Nigeria Union Conference (WNUC)')
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Western Nigeria Union (WNUC)
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Calendar Grid */}
        <CalendarGrid
          events={filteredEvents}
          viewMode={viewMode}
          currentDate={currentDate}
          onEventClick={setSelectedEvent}
          levelColors={levelColors}
        />

        {/* Legend */}
        <div className="bg-white rounded-xl shadow-md p-6 mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Event Priority Legend</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-slate-800"></div>
              <div>
                <div className="text-sm font-semibold text-gray-900">General Conference</div>
                <div className="text-xs text-gray-600">Highest priority - Global events</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-blue-600"></div>
              <div>
                <div className="text-sm font-semibold text-gray-900">Division</div>
                <div className="text-xs text-gray-600">West-Central Africa events</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-amber-600"></div>
              <div>
                <div className="text-sm font-semibold text-gray-900">Union</div>
                <div className="text-xs text-gray-600">Nigeria Union Mission events</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-emerald-600"></div>
              <div>
                <div className="text-sm font-semibold text-gray-900">National</div>
                <div className="text-xs text-gray-600">Nigeria public holidays</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          levelColors={levelColors}
        />
      )}

      <Footer />
    </div>
  );
}
